import { useEffect, useState } from 'react';
import { Save, Settings2, X } from 'lucide-react';
import type { TargetConfig } from '../types';

interface TargetDrawerProps {
  open: boolean;
  targets: TargetConfig;
  products: string[];
  onClose: () => void;
  onSave: (targets: TargetConfig) => void;
}

export default function TargetDrawer({ open, targets, products, onClose, onSave }: TargetDrawerProps) {
  const [draft, setDraft] = useState(targets);
  useEffect(() => setDraft(targets), [targets, open]);
  if (!open) return null;

  const numberField = (key: keyof Pick<TargetConfig, 'annualSalesTarget' | 'annualProductQuantityTarget' | 'activeTerminalTarget'>, value: string) => {
    setDraft((current) => ({ ...current, [key]: Math.max(0, Number(value) || 0) }));
  };

  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="target-title">
        <div className="drawer-head">
          <div>
            <div className="modal-kicker"><Settings2 size={16} /> 指标口径</div>
            <h2 id="target-title">目标设置</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        </div>
        <p className="drawer-copy">样表中没有数值目标，因此目标由你配置并保存在当前浏览器。销售额与单品数量会按年度、季度、月度自动折算。</p>
        <label className="field">
          <span>年度总销售额目标（元）</span>
          <input type="number" min="0" step="10000" value={draft.annualSalesTarget} onChange={(event) => numberField('annualSalesTarget', event.target.value)} />
        </label>
        <label className="field">
          <span>重点跟进单品</span>
          <select value={draft.targetProduct} onChange={(event) => setDraft((current) => ({ ...current, targetProduct: event.target.value }))}>
            {products.map((product) => <option key={product} value={product}>{product}</option>)}
          </select>
        </label>
        <label className="field">
          <span>重点单品年度数量目标</span>
          <input type="number" min="0" step="10" value={draft.annualProductQuantityTarget} onChange={(event) => numberField('annualProductQuantityTarget', event.target.value)} />
        </label>
        <label className="field">
          <span>活跃终端目标（家）</span>
          <input type="number" min="0" step="1" value={draft.activeTerminalTarget} onChange={(event) => numberField('activeTerminalTarget', event.target.value)} />
        </label>
        <div className="target-rule-note">
          <strong>折算规则</strong>
          <span>季度目标 = 年度目标 ÷ 4；月度目标 = 年度目标 ÷ 12；活跃终端目标在各周期保持不变。</span>
        </div>
        <button
          type="button"
          className="primary-button drawer-save"
          onClick={() => onSave({ ...draft, updatedAt: new Date().toISOString() })}
        >
          <Save size={17} /> 保存并重新计算
        </button>
      </aside>
    </div>
  );
}
